import { jsPDF } from 'jspdf';
import colombiaMapService from '../../../services/ColombiaMapService';

export const useUtils = () => {
  const capitalizarPrimeraLetra = (cadena: string) =>
    (cadena || "")
      .split(" ")
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(" ");

  const copyUrl = (id: string) => {
    const url = `${window.location.protocol}//${window.location.host}/?id=${id}`;
    navigator.clipboard
      .writeText(url)
      .then(() => colombiaMapService.setShowAlert(url))
      .catch((err) => console.error("Error al copiar el texto: ", err));
  };

  const generatePdf = (itm: any) => {
    try {
      const doc = new jsPDF();
      const W = doc.internal.pageSize.getWidth();
      let y = 10;
      const margin = 20;

      const drawRule = () => {
        doc.setDrawColor(206, 0, 71);
        doc.setLineWidth(0.4);
        doc.line(margin, y, W - margin, y);
        y += 20;
      };

      // Título
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(206, 0, 71);
      doc.text("Mapa de Cuidados de Colombia", margin, y);
      y += 15;
      drawRule();

      const nombre = itm?.properties?.["Nombre"] || "Centro sin nombre";
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      const lineasNombre = doc.splitTextToSize(nombre, W - margin * 2);
      lineasNombre.forEach((linea: string) => {
        doc.text(linea, margin, y);
        y += 8;
      });
      y += 2;

      // Contenido base
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);

      // Dirección
      const direccion = itm?.properties?.["Domicilios completos"] || "";
      if (direccion) {
        doc.setFont("helvetica", "bold");
        doc.text("Dirección:", margin, y);
        doc.setFont("helvetica", "normal");
        const lineas = doc.splitTextToSize(capitalizarPrimeraLetra(direccion), W - margin * 2 - 30);
        lineas.forEach((linea: string) => {
          doc.text(linea, margin + 30, y);
          y += 6;
        });
        y += 5;
      }

      // Depto/Municipio
      const muniTxt = itm?.properties?.["MUNICIPIO.1"] || "";
      const [municipio, departamento] = muniTxt.split(",").map((s: string) => (s || "").trim());

      if (departamento) {
        doc.setFont("helvetica", "bold");
        doc.text("Departamento:", margin, y);
        doc.setFont("helvetica", "normal");
        const lineasDpto = doc.splitTextToSize(capitalizarPrimeraLetra(departamento), W - margin * 2 - 40);
        lineasDpto.forEach((linea: string) => {
          doc.text(linea, margin + 40, y);
          y += 6;
        });
        y += 2;
      }
      if (municipio) {
        doc.setFont("helvetica", "bold");
        doc.text("Municipio:", margin, y);
        doc.setFont("helvetica", "normal");
        const lineasMuni = doc.splitTextToSize(capitalizarPrimeraLetra(municipio), W - margin * 2 - 30);
        lineasMuni.forEach((linea: string) => {
          doc.text(linea, margin + 30, y);
          y += 6;
        });
        y += 2;
      }

      const poblacion = itm?.properties?.["Población atendida"] || "";
      if (poblacion) {
        doc.setFont("helvetica", "bold");
        doc.text("Población Atendida:", margin, y);
        doc.setFont("helvetica", "normal");
        const lineasPob = doc.splitTextToSize(capitalizarPrimeraLetra(poblacion), W - margin * 2 - 50);
        lineasPob.forEach((linea: string) => {
          doc.text(linea, margin + 50, y);
          y += 6;
        });
        y += 4;
      }

      // Servicio
      const servicio = itm?.properties?.["Servicio Ofrecido"] || "";
      if (servicio) {
        doc.setFont("helvetica", "bold");
        doc.text("Servicio Ofrecido:", margin, y);
        doc.setFont("helvetica", "normal");
        const lineasServ = doc.splitTextToSize(servicio, W - margin * 2 - 50);
        lineasServ.forEach((linea: string) => {
          doc.text(linea, margin + 50, y);
          y += 6;
        });
        y += 5;
        drawRule();
      }

      // Códigos
      const sigep = itm?.properties?.["COD SIGEP"] || "";
      const ciiu = itm?.properties?.["Código CIIU"] || "";
      if (sigep || ciiu) {
        doc.setFont("helvetica", "bold");
        doc.text("Códigos:", margin, y);
        doc.setFont("helvetica", "normal");
        y += 6;
        if (sigep) {
          const lineasSigep = doc.splitTextToSize(`SIGEP: ${sigep}`, W - margin * 2 - 10);
          lineasSigep.forEach((linea: string) => { doc.text(linea, margin + 10, y); y += 6; });
        }
        if (ciiu) {
          const lineasCiiu = doc.splitTextToSize(`CIIU: ${ciiu}`, W - margin * 2 - 10);
          lineasCiiu.forEach((linea: string) => { doc.text(linea, margin + 10, y); y += 6; });
        }
        y += 2;
      }

      // Accesibilidad
      const accSilla = itm?.properties?.["Accesibilidad sillas de ruedas"];
      const accVisual = itm?.properties?.["Accesibilidad personas ciegas"];
      if ((accSilla && accSilla !== "No") || (accVisual && accVisual !== "No")) {
        doc.setFont("helvetica", "bold");
        doc.text("Accesibilidad:", margin, y);
        doc.setFont("helvetica", "normal");
        y += 6;
        if (accSilla && accSilla !== "No")  { doc.text("• Accesible con silla de ruedas", margin + 10, y); y += 6; }
        if (accVisual && accVisual !== "No"){ doc.text("• Accesible para personas con discapacidad visual", margin + 10, y); y += 6; }
        y += 5;
      }

      // Horarios
      const diasAtencion = itm?.properties?.["Días de atención"] || "";
      const horaInicio = itm?.properties?.["Horario de inicio de la atención"] || "";
      const horaFin = itm?.properties?.["Horario de finalización de la atención"] || "";
      if (diasAtencion || (horaInicio && horaFin)) {
        doc.setFont("helvetica", "bold");
        doc.text("Horarios:", margin, y);
        doc.setFont("helvetica", "normal");
        y += 6;
        if (diasAtencion) {
          const lineasDias = doc.splitTextToSize(`Días: ${diasAtencion}`, W - margin * 2 - 10);
          lineasDias.forEach((linea: string) => { doc.text(linea, margin + 10, y); y += 6; });
        }
        if (horaInicio && horaFin) {
          const lineasHorario = doc.splitTextToSize(`Horario: ${horaInicio} a ${horaFin}`, W - margin * 2 - 10);
          lineasHorario.forEach((linea: string) => { doc.text(linea, margin + 10, y); y += 6; });
        }
        y += 2;
      }

      const telefono = String(itm?.properties?.["Teléfono del Centro de Cuidado"] || "").trim();
      const email = String(itm?.properties?.["Correo electrónico del Centro de Cuidado"] || "").trim();
      const web = String(itm?.properties?.["Página web del Centro de Cuidado"] || "").trim();
      if (telefono || email || web) {
        doc.setFont("helvetica", "bold");
        doc.text("Contacto:", margin, y);
        doc.setFont("helvetica", "normal");
        y += 6;
        if (telefono) {
          const lineasTel = doc.splitTextToSize(`Teléfono: ${telefono}`, W - margin * 2 - 10);
          lineasTel.forEach((linea: string) => { doc.text(linea, margin + 10, y); y += 6; });
        }
        if (email) {
          const lineasEmail = doc.splitTextToSize(`Email: ${email}`, W - margin * 2 - 10);
          lineasEmail.forEach((linea: string) => { doc.text(linea, margin + 10, y); y += 6; });
        }
        if (web) {
          const lineasWeb = doc.splitTextToSize(`Web: ${web}`, W - margin * 2 - 10);
          lineasWeb.forEach((linea: string) => { doc.text(linea, margin + 10, y); y += 6; });
        }
        y += 2;
      }

      // Nota de ubicación
      const notaUbicacion = itm?.properties?.["NOTA UBICACIÓN"] || "";
      if (notaUbicacion) {
        doc.setFont("helvetica", "bold");
        doc.text("Nota de Ubicación:", margin, y);
        y += 6;
        doc.setFont("helvetica", "normal");
        const lineasNota = doc.splitTextToSize(notaUbicacion, W - margin * 2 - 10);
        lineasNota.forEach((linea: string) => { doc.text(linea, margin + 10, y); y += 6; });
        y += 8;
      }

      const url = `${window.location.protocol}//${window.location.host}/?id=${itm?.properties?.ID}`;
      doc.setTextColor(0, 0, 255);
      doc.setFont("helvetica", "bold");
      doc.textWithLink("Ver en el mapa", margin, y, { url });
      doc.setTextColor(0, 0, 0);

      doc.save(`Mapa de Cuidados - ${nombre}.pdf`);
    } catch (e) {
      console.error("Error generando PDF:", e);
    }
  };

  return {
    capitalizarPrimeraLetra,
    copyUrl,
    generatePdf,
  };
};
